import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Observable, Subject, Subscription, forkJoin, interval, of} from 'rxjs';
import {catchError, take, takeUntil} from 'rxjs/operators';
import {Select, Store} from '@ngxs/store';
import {environment} from '@env/environment';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {PrinterClientService} from '@app/service/printer-client.service';
import {SocketIoService} from '@app/service/socket-io.service';
import {EventEnum} from '@app/enums/event.enum';
import {LoginService} from '@app/service/login.service';
import {SesionState} from '@app/state/sesion/sesion.state';
import {Office} from '@app/domain/office';
import {OfficeService} from '@app/service/office.service';
import {UpdateOfficeCode} from '@app/state/sesion/sesion.actions';
import {
    LocalPrinterClientStatus,
    PrinterClient,
    PrinterClientBlock,
    PrinterClientCommand,
    PrinterClientCommandResult,
    PrinterPairingCode,
    PrinterDevice
} from '@app/domain/printer-client';

interface PrinterClientRow extends PrinterClient {
    connected: boolean;
    lastSeenAt?: string;
}

/** Instalador del servicio de impresión para un sistema operativo. */
interface InstallerOption {
    os: 'windows' | 'macArm64' | 'macX64' | 'linux';
    label: string;
    url: string;
}

const INSTALLER_LABELS: Record<InstallerOption['os'], string> = {
    windows: 'Windows',
    macArm64: 'Mac (Apple Silicon)',
    macX64: 'Mac (Intel)',
    linux: 'Linux (.deb)'
};

interface PrinterClientGroup {
    officeCode: string;
    clients: PrinterClientRow[];
    /** Jars antiguos no bloqueados: si hay más de uno, recibe el que se conectó primero. */
    legacyActive: number;
}

/**
 * Panel "Equipos de impresión": qué PCs (jars) están conectadas por punto de venta, con qué impresoras, y acciones
 * remotas (probar, marcar principal, reiniciar, desactivar; bloquear los jars antiguos que no aceptan comandos).
 */
@Component({
    selector: 'app-printer-clients-dialog',
    templateUrl: './printer-clients-dialog.component.html',
    styleUrls: ['./printer-clients-dialog.component.scss']
})
export class PrinterClientsDialogComponent implements OnInit, OnDestroy {

    @Select(SesionState.getOffice) office$: Observable<Office>;

    /** Marca para los textos (Teki / Cbetcloud): el mismo servicio de impresión sirve a las dos. */
    readonly brand: string = environment.company || 'Teki';
    /** Instaladores por sistema (última versión). Sin instaladores en el environment = sin botón de descarga. */
    readonly installers: InstallerOption[] = Object.entries(environment.printerInstallers || {})
        .filter(([, url]) => !!url)
        .map(([os, url]) => ({os: os as InstallerOption['os'], label: INSTALLER_LABELS[os] || os, url: url as string}));
    /** El del sistema de este navegador; el resto va en el desplegable. */
    preferredInstaller: InstallerOption = null;
    installerMenu: MenuItem[] = [];

    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    groups: PrinterClientGroup[] = [];
    blocks: PrinterClientBlock[] = [];
    loading = false;
    loaded = false;
    loadError = '';
    devicesError = false;
    lastUpdated: Date = null;
    addMode: 'local' | 'remote' = 'local';
    actionError = '';
    pairingUpdates: PrinterDevice[] = [];
    actionMenuItems: MenuItem[] = [];
    now = Date.now();
    /** socketId con una acción en curso, para deshabilitar sus botones. */
    busySocketId: string = null;
    testMenuItems: MenuItem[] = [];
    /** Equipos vinculados con código (backend): solo esos se pueden quitar revocando su token. */
    pairedDeviceIds = new Set<string>();

    /** Vista "Agregar equipo". */
    adding = false;
    offices: Office[] = [];
    selectedOfficeId: number;
    readonly officeCodeFormatHint = 'Usa de 1 a 50 letras sin tildes, números o guiones (-), sin espacios.';
    officeCodeDraft = '';
    officeCodeError = '';
    savingOfficeCode = false;
    canDefineOfficeCode = false;
    pairingCode: PrinterPairingCode = null;
    /** El ícono de copiar pasa a ✓ unos segundos después de copiar. */
    codeCopied = false;
    generating = false;
    /** Servicio de impresión instalado en esta PC; null si no se detectó. */
    localClient: LocalPrinterClientStatus = null;
    detectingLocal = false;
    pairingLocal = false;

    private devices: PrinterDevice[] = [];
    private pairingBaseline = new Map<string, string>();
    private codeRequest = 0;
    private pairingStartedAt = 0;
    private pairingTimer: Subscription;
    private loadSubscription: Subscription;
    private reloadPending = false;
    private copyTimer: ReturnType<typeof setTimeout>;
    private unsubscribe$ = new Subject<void>();

    constructor(private printerClientService: PrinterClientService,
                private socketIoService: SocketIoService,
                private messageService: MessageService,
                private confirmationService: ConfirmationService,
                private loginService: LoginService,
                private officeService: OfficeService,
                private store: Store) {
    }

    ngOnInit(): void {
        this.selectInstaller(this.detectOs());
        this.refineMacArchitecture();
        this.offices = this.loginService.getPuntosVentaUsuario() || [];
        this.socketIoService.onEvent(EventEnum.PRINTER_CLIENTS_CHANGED)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                if (this.visible) {
                    this.load();
                }
            });
    }

    ngOnDestroy(): void {
        this.resetPairing();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    onShow(): void {
        this.loadSubscription?.unsubscribe();
        this.loading = false;
        this.reloadPending = false;
        this.devicesError = false;
        this.adding = false;
        this.resetPairing();
        this.offices = this.loginService.getPuntosVentaUsuario() || [];
        this.devices = [];
        this.groups = [];
        this.blocks = [];
        this.pairedDeviceIds.clear();
        this.loaded = false;
        this.lastUpdated = null;
        this.load();
    }

    onHide(): void {
        this.loadSubscription?.unsubscribe();
        this.loading = false;
        this.reloadPending = false;
        this.resetPairing();
    }

    close(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    load(): void {
        if (this.loading) {
            this.reloadPending = true;
            return;
        }
        this.loading = true;
        this.loadError = '';
        this.loadSubscription = forkJoin({
            list: this.printerClientService.findAll(),
            devices: this.printerClientService.findPairedDevices().pipe(catchError(() => of(null)))
        }).pipe(takeUntil(this.unsubscribe$)).subscribe(({list, devices}) => {
            this.devicesError = devices === null;
            // Conserva los equipos conocidos si falla esta parte de la actualización.
            if (devices !== null) {
                this.devices = devices;
            }
            const clients = list.clients || [];
            const connectedIds = new Set(clients.map(client => client.deviceId).filter(Boolean));
            const rows: PrinterClientRow[] = clients.map(client => ({...client, connected: true}));
            this.devices.filter(device => !connectedIds.has(device.deviceId)).forEach(device => rows.push({
                socketId: null, deviceId: device.deviceId, deviceName: device.deviceName, officeCode: device.officeCode,
                legacy: false, blocked: false, isDefault: false, printers: [], version: null, ip: null,
                connectedAt: null, connected: false, lastSeenAt: device.lastSeenAt
            }));
            this.groups = this.groupByOfficeCode(rows);
            this.blocks = list.blocks || [];
            this.pairedDeviceIds = new Set(this.devices.map(device => device.deviceId));
            if (this.adding && this.pairingCode && devices !== null) {
                this.pairingUpdates = devices.filter(device => device.officeCode === this.pairingCode.officeCode
                    && new Date(device.pairedAt).getTime() >= this.pairingStartedAt
                    && device.pairedAt !== this.pairingBaseline.get(device.deviceId));
            }
            this.lastUpdated = new Date();
            this.loaded = true;
            this.finishLoad();
        }, () => {
            this.loadError = this.loaded
                ? 'No pudimos actualizar los equipos. La información mostrada corresponde a la última consulta.'
                : 'No pudimos cargar los equipos. Comprueba tu conexión y vuelve a intentarlo.';
            this.finishLoad();
        });
    }

    private finishLoad(): void {
        this.loading = false;
        if (this.reloadPending) {
            this.reloadPending = false;
            this.load();
        }
    }

    officeName(code: string): string {
        return this.offices.find(office => office.codigo === code)?.nombre || 'Punto de venta ' + code;
    }

    get selectedOffice(): Office {
        return this.offices.find(office => office.id === this.selectedOfficeId);
    }

    get officeCode(): string {
        return (this.selectedOffice?.codigo || '').trim();
    }

    get localClientAlreadyPaired(): boolean {
        return !!this.localClient?.paired && this.localClient.officeCode === this.officeCode;
    }

    addAnotherComputer(): void {
        if (!this.officeCode || this.pairingLocal || this.savingOfficeCode) {
            return;
        }
        this.setAddMode('remote');
        this.generateCode();
    }

    onOfficeChange(): void {
        this.resetPairing();
        this.officeCodeDraft = '';
        this.officeCodeError = '';
    }

    get validOfficeCodeDraft(): boolean {
        const code = this.officeCodeDraft;
        return code.length > 0 && code.length <= 50 && !/[^A-Za-z0-9-]/.test(code);
    }

    validateOfficeCodeDraft(): void {
        this.officeCodeError = this.officeCodeDraft && !this.validOfficeCodeDraft ? this.officeCodeFormatHint : '';
    }

    saveOfficeCode(): void {
        const office = this.selectedOffice;
        if (!office || this.officeCode || this.savingOfficeCode || !this.canDefineOfficeCode) {
            return;
        }
        const codigo = this.officeCodeDraft;
        if (!this.validOfficeCodeDraft) {
            this.officeCodeError = this.officeCodeFormatHint;
            return;
        }
        this.officeCodeError = '';
        this.savingOfficeCode = true;
        this.officeService.assignCode(office.id, codigo).pipe(takeUntil(this.unsubscribe$)).subscribe(updated => {
            this.offices = this.offices.map(item => item.id === updated.id ? {...item, codigo: updated.codigo} : item);
            this.store.dispatch(new UpdateOfficeCode(updated.id, updated.codigo));
            this.savingOfficeCode = false;
            this.onOfficeChange();
            this.messageService.add({severity: updated.codigo === codigo ? 'success' : 'info',
                summary: updated.codigo === codigo ? 'Código guardado' : 'El punto de venta ya tiene código',
                detail: `${office.nombre}: ${updated.codigo}. Ya puedes vincular el equipo.`});
        }, error => {
            this.savingOfficeCode = false;
            this.officeCodeError = error.status === 403
                ? 'Necesitas permiso para editar puntos de venta. Solicita a un administrador que defina el código.'
                : error.error?.message || error.error?.mensaje || 'No pudimos guardar el código. Vuelve a intentarlo.';
        });
    }

    get codeExpired(): boolean {
        return !!this.pairingCode && new Date(this.pairingCode.expiresAt).getTime() <= this.now;
    }

    resetPairing(): void {
        this.pairingTimer?.unsubscribe();
        clearTimeout(this.copyTimer);
        this.codeRequest++;
        this.pairingCode = null;
        this.pairingUpdates = [];
        this.codeCopied = false;
        this.generating = false;
        this.actionError = '';
    }

    setAddMode(mode: 'local' | 'remote'): void {
        this.addMode = mode;
        this.resetPairing();
    }

    startAdding(): void {
        this.adding = true;
        this.addMode = 'local';
        this.resetPairing();
        this.offices = this.loginService.getPuntosVentaUsuario() || [];
        this.canDefineOfficeCode = this.loginService.hasPermission('PUNTO_VENTA_EDITAR');
        this.officeCodeDraft = '';
        this.officeCodeError = '';
        this.office$.pipe(take(1), takeUntil(this.unsubscribe$))
            .subscribe(office => this.selectedOfficeId = this.offices.some(item => item.id === office?.id)
                ? office.id : this.offices[0]?.id);
        this.detectLocalClient();
    }

    stopAdding(): void {
        this.adding = false;
        this.resetPairing();
        this.load();
    }

    detectLocalClient(): void {
        if (this.detectingLocal) {
            return;
        }
        this.localClient = null;
        this.detectingLocal = true;
        this.printerClientService.localStatus().pipe(takeUntil(this.unsubscribe$)).subscribe(status => {
            this.localClient = status;
            this.detectingLocal = false;
        }, () => {
            this.localClient = null;
            this.detectingLocal = false;
        });
    }

    generateCode(): void {
        if (!this.officeCode || this.generating) {
            return;
        }
        const request = ++this.codeRequest;
        this.generating = true;
        this.pairingStartedAt = Date.now();
        this.pairingTimer?.unsubscribe();
        this.actionError = '';
        this.pairingCode = null;
        this.pairingUpdates = [];
        this.pairingBaseline = new Map(this.devices.map(device => [device.deviceId, device.pairedAt]));
        this.printerClientService.createPairingCode(this.officeCode).pipe(takeUntil(this.unsubscribe$)).subscribe(code => {
            if (request !== this.codeRequest) {
                return;
            }
            this.pairingCode = code;
            this.now = Date.now();
            this.codeCopied = false;
            this.generating = false;
            this.pairingTimer = interval(1000).pipe(takeUntil(this.unsubscribe$)).subscribe(tick => {
                this.now = Date.now();
                if (this.codeExpired) {
                    this.pairingTimer.unsubscribe();
                } else if (tick % 5 === 4 && this.visible && this.adding && !this.pairingUpdates.length
                    && !this.loadError && !this.devicesError) {
                    this.load();
                }
            });
        }, () => {
            if (request === this.codeRequest) {
                this.generating = false;
                this.actionError = 'No pudimos generar el código. Vuelve a intentarlo.';
            }
        });
    }

    downloadInstaller(installer: InstallerOption): void {
        if (installer?.url) {
            // El archivo se sirve como attachment: el navegador lo descarga sin salir de la página
            window.location.href = installer.url;
        }
    }

    private selectInstaller(os: InstallerOption['os']): void {
        this.preferredInstaller = this.installers.find(i => i.os === os) || this.installers[0] || null;
        this.installerMenu = this.installers
            .filter(i => i !== this.preferredInstaller)
            .map(i => ({label: i.label, icon: 'pi pi-download', command: () => this.downloadInstaller(i)}));
    }

    private detectOs(): InstallerOption['os'] {
        const nav = navigator as any;
        const platform = (nav.userAgentData?.platform || navigator.platform || navigator.userAgent || '').toLowerCase();
        if (platform.includes('mac')) {
            return 'macArm64';
        }
        if (platform.includes('linux') && !/android/i.test(navigator.userAgent)) {
            return 'linux';
        }
        return 'windows';
    }

    /** Safari no distingue Apple Silicon de Intel; Chrome/Edge sí lo informan (architecture "arm" | "x86"). */
    private refineMacArchitecture(): void {
        const uaData = (navigator as any).userAgentData;
        if (this.preferredInstaller?.os !== 'macArm64' || !uaData?.getHighEntropyValues) {
            return;
        }
        uaData.getHighEntropyValues(['architecture'])
            .then((values: { architecture?: string }) => {
                if (values?.architecture === 'x86') {
                    this.selectInstaller('macX64');
                }
            })
            .catch(() => undefined);
    }

    copyCode(): void {
        const code = this.pairingCode?.code;
        if (!code || this.codeExpired) {
            return;
        }
        const done = () => {
            if (this.pairingCode?.code !== code) {
                return;
            }
            this.codeCopied = true;
            this.messageService.add({severity: 'success', summary: 'Código copiado', detail: code, life: 2000});
            clearTimeout(this.copyTimer);
            this.copyTimer = setTimeout(() => this.codeCopied = false, 2000);
        };
        const fallback = () => {
            if (this.pairingCode?.code !== code) {
                return;
            }
            if (this.copyWithTextarea(code)) {
                done();
            } else {
                this.actionError = 'No pudimos copiar el código. Selecciónalo y cópialo manualmente.';
            }
        };
        // navigator.clipboard solo existe en https/localhost; si no está, se copia con un textarea temporal
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(code).then(done, fallback);
        } else {
            fallback();
        }
    }

    private copyWithTextarea(text: string): boolean {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        const focused = document.activeElement as HTMLElement;
        try {
            textarea.select();
            return document.execCommand('copy');
        } catch {
            return false;
        } finally {
            textarea.remove();
            focused?.focus();
        }
    }

    /** Vincula el servicio de esta PC sin escribir el código: se genera y se envía al jar por localhost. */
    pairThisComputer(): void {
        if (!this.officeCode || !this.localClient || this.pairingLocal || this.localClientAlreadyPaired) {
            return;
        }
        const officeCode = this.officeCode;
        this.actionError = '';
        this.pairingLocal = true;
        this.printerClientService.createPairingCode(officeCode).pipe(takeUntil(this.unsubscribe$)).subscribe(code => {
            this.printerClientService.localPair(code.code).pipe(takeUntil(this.unsubscribe$)).subscribe(resp => {
                this.pairingLocal = false;
                if (resp?.ok) {
                    this.messageService.add({severity: 'success',
                        summary: this.localClient.paired ? 'Punto de venta del equipo actualizado' : 'Equipo vinculado',
                        detail: `${this.localClient.deviceName} quedó vinculado a ${this.officeName(resp.officeCode || officeCode)}. `
                            + 'La sede activa de Teki no cambió.'});
                    this.stopAdding();
                } else {
                    this.actionError = resp?.message || 'No pudimos vincular esta PC. Vuelve a intentarlo.';
                    this.messageService.add({severity: 'warn', summary: 'No se pudo vincular', detail: this.actionError});
                }
            }, () => {
                this.pairingLocal = false;
                this.actionError = 'El servicio no respondió. Comprueba que esté abierto y vuelve a buscarlo.';
                this.messageService.add({severity: 'warn', summary: 'No se pudo vincular',
                    detail: 'El servicio de impresión de esta PC no respondió.'});
            });
        }, () => {
            this.pairingLocal = false;
            this.actionError = 'No pudimos iniciar la vinculación. Vuelve a intentarlo.';
        });
    }

    canRemove(client: PrinterClient): boolean {
        return !client.legacy && !!client.deviceId && this.pairedDeviceIds.has(client.deviceId);
    }

    /** Revoca el token del equipo y le pide al jar que borre su configuración. */
    remove(client: PrinterClientRow): void {
        if (this.busySocketId || !this.canRemove(client)) {
            return;
        }
        this.confirmationService.confirm({
            message: `¿Quitar <b>${client.deviceName}</b>? Dejará de imprimir hasta que lo vincules con un código nuevo.`,
            accept: () => {
                this.actionError = '';
                this.busySocketId = client.socketId || client.deviceId;
                this.printerClientService.revokeDevice(client.deviceId).pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
                    const unlink = client.connected
                        ? this.printerClientService.command(client.socketId, 'unlink').pipe(catchError(() => of(null)))
                        : of(null);
                    unlink.pipe(takeUntil(this.unsubscribe$))
                        .subscribe(() => {
                            this.busySocketId = null;
                            this.messageService.add({severity: 'success', summary: 'Equipo quitado', detail: client.deviceName});
                            this.load();
                        });
                }, () => this.failAction('No pudimos quitar el equipo. Vuelve a intentarlo.'));
            }
        });
    }

    openActionMenu(menu: any, event: Event, client: PrinterClientRow): void {
        this.actionMenuItems = client.connected ? [
            {label: client.isDefault ? 'Quitar como principal' : 'Usar como principal', icon: 'pi pi-star',
                command: () => this.toggleDefault(client)},
            {label: 'Reiniciar conexión', icon: 'pi pi-replay', command: () => this.restart(client)},
            {label: 'Desactivar servicio', icon: 'pi pi-power-off', command: () => this.disable(client)}
        ] : [];
        if (this.canRemove(client)) {
            this.actionMenuItems.push({label: 'Quitar equipo', icon: 'pi pi-trash', command: () => this.remove(client)});
        }
        menu.toggle(event);
    }

    openTestMenu(menu: any, event: Event, client: PrinterClient): void {
        this.testMenuItems = client.printers.map(printerName => ({
            label: printerName,
            icon: 'pi pi-print',
            command: () => this.runCommand(client, 'test-print', {printerName})
        }));
        menu.toggle(event);
    }

    toggleDefault(client: PrinterClient): void {
        this.runCommand(client, 'set-default', {isDefault: !client.isDefault});
    }

    restart(client: PrinterClient): void {
        this.runCommand(client, 'restart');
    }

    disable(client: PrinterClient): void {
        this.confirmationService.confirm({
            message: `¿Desactivar el servicio de impresión de <b>${client.deviceName}</b>? Dejará de recibir impresiones `
                + 'y solo se puede volver a iniciar desde la ventana del servicio en esa PC.',
            accept: () => this.runCommand(client, 'disable')
        });
    }

    block(client: PrinterClient): void {
        if (this.busySocketId) {
            return;
        }
        this.confirmationService.confirm({
            message: `¿Bloquear el cliente antiguo de <b>${client.officeCode}</b> (IP ${client.ip})? Seguirá conectado `
                + 'pero dejará de recibir impresiones. Se puede desbloquear desde este panel.',
            accept: () => {
                this.actionError = '';
                this.busySocketId = client.socketId || client.deviceId;
                this.printerClientService.block(client.socketId).pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
                    this.busySocketId = null;
                    this.messageService.add({severity: 'success', summary: 'Cliente bloqueado', detail: client.officeCode});
                    this.load();
                }, () => this.failAction('No pudimos bloquear el equipo. Vuelve a intentarlo.'));
            }
        });
    }

    unblock(rule: PrinterClientBlock): void {
        if (this.busySocketId) {
            return;
        }
        this.actionError = '';
        this.busySocketId = rule.id;
        this.printerClientService.unblock(rule.id).pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
            this.busySocketId = null;
            this.messageService.add({severity: 'success', summary: 'Bloqueo eliminado', detail: rule.officeCode});
            this.load();
        }, () => this.failAction('No pudimos desbloquear el equipo. Vuelve a intentarlo.'));
    }

    trackBySocket(index: number, client: PrinterClient): string {
        return client.socketId || client.deviceId;
    }

    private failAction(message: string): void {
        this.busySocketId = null;
        this.actionError = message;
    }

    private runCommand(client: PrinterClient, command: PrinterClientCommand, payload?: object): void {
        if (this.busySocketId) {
            return;
        }
        this.actionError = '';
        this.busySocketId = client.socketId || client.deviceId;
        this.printerClientService.command(client.socketId, command, payload).pipe(takeUntil(this.unsubscribe$))
            .subscribe((resp: PrinterClientCommandResult) => {
                this.busySocketId = null;
                this.messageService.add({
                    severity: resp?.ok ? 'success' : 'warn',
                    summary: client.deviceName || client.officeCode,
                    detail: resp?.message || (resp?.ok ? 'Listo' : 'No se pudo completar la acción')
                });
                if (!resp?.ok) {
                    this.actionError = resp?.message || 'No pudimos completar la acción. Vuelve a intentarlo.';
                }
                this.load();
            }, () => this.failAction('El equipo no respondió. Actualiza la lista y vuelve a intentarlo.'));
    }

    private groupByOfficeCode(clients: PrinterClientRow[]): PrinterClientGroup[] {
        const byCode = new Map<string, PrinterClientRow[]>();
        clients.forEach(client => {
            const list = byCode.get(client.officeCode) || [];
            list.push(client);
            byCode.set(client.officeCode, list);
        });
        return Array.from(byCode.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([officeCode, list]) => ({
                officeCode,
                clients: list,
                legacyActive: list.filter(c => c.legacy && !c.blocked).length
            }));
    }
}
